/* global axios */
import ApiClient from '../ApiClient';
import ContactsAPI from '../contacts';
import getUuid from 'widget/helpers/uuid';
import {
  CALL_WARNING_CODES,
  SDK_WARNING_TO_CALL_WARNING,
} from 'dashboard/helper/voice';

// Keep the per-tab id within the backend nonce cap (Voice::TokenService::CLIENT_SESSION_MAX_LENGTH).
const VOICE_TAB_ID_MAX_LENGTH = 16;

// Consecutive samples (~1 per second) with NO inbound RTP progress before we flag dead
// audio. Deliberately counted on media arrival, not on amplitude - see wireCallDiagnostics.
const STALLED_MEDIA_SAMPLE_LIMIT = 5;

// How many declined call SIDs to remember (see rejectedCallSids). Bounded so a long shift
// cannot grow the set forever; this is far more declines than could plausibly still have a
// join in flight, and the entries evicted are always the oldest.
const REJECTED_CALL_SID_LIMIT = 32;

// First argument that is a real number (Number.isFinite rejects undefined / null / NaN /
// Infinity), or null when none of them is.
const firstFiniteNumber = (...values) => {
  const found = values.find(value => Number.isFinite(value));
  return found === undefined ? null : found;
};

// Read inbound-RTP progress out of a Twilio RTCSample. Verified against the installed
// @twilio/voice-sdk 2.12.4 (es5/twilio/rtc/sample.d.ts and StatsMonitor#_createSample):
// the two shapes carry DIFFERENT things and must not be conflated.
//   sample.totals.{packetsReceived,bytesReceived} - CUMULATIVE since the leg came up
//   sample.{packetsReceived,bytesReceived}        - the DELTA for the last second only
// Returns { cumulative } when the totals block is readable, else { delta } from the
// per-second fields, else null when neither is. A sample carrying no counters at all (a bare
// test double, or an SDK build without them) is absence of evidence, never evidence of
// failure, so the caller must not act on it.
const readInboundMedia = sample => {
  const totals = sample?.totals;
  const cumulative = firstFiniteNumber(
    totals?.packetsReceived,
    totals?.bytesReceived
  );
  if (cumulative !== null) return { cumulative };
  const delta = firstFiniteNumber(
    sample?.packetsReceived,
    sample?.bytesReceived
  );
  if (delta !== null) return { delta };
  return null;
};

// Device.State values a warm device may still be reused from. The full SDK 2.x enum is
// { destroyed, unregistered, registering, registered } - there is NO 'error' state, so the
// `state !== 'error'` check this replaces was always true and happily handed back a
// DESTROYED device (whose connect() throws) or one that had lost its signaling
// registration (whose connect() has no transport to dial over). 'registering' IS reusable:
// it is the transient state of a device the SDK is re-registering after a transport blip,
// and rebuilding then would destroy a device that is about to be healthy again - along
// with any call still running on it.
const REUSABLE_DEVICE_STATES = Object.freeze(['registered', 'registering']);

class VoiceAPI extends ApiClient {
  constructor() {
    super('voice', { accountScoped: true });
    this.device = null;
    this.activeConnection = null;
    // The in-flight device.connect() promise while a join is dialing. Captured
    // synchronously so a concurrent caller returns it instead of opening a second leg.
    this.pendingConnection = null;
    // The conference `To` string of the active / in-flight connection. A concurrent
    // join for the SAME To is idempotent (returns the existing leg); a DIFFERENT To
    // must not reuse the wrong leg (that would phantom-answer another call).
    this.activeConnectionTo = null;
    this.pendingConnectionTo = null;
    this.initialized = false;
    // The inbox the current device was initialized for. A token is inbox-specific (TwiML app
    // SID + callback endpoints), so the warm device may only be reused for the SAME inbox.
    this.deviceInboxId = null;
    // In-flight initializeDevice promise, shared by concurrent callers so a race (e.g. a direct
    // outbound placement vs the WS auto-join) can't create two Devices.
    this.initPromise = null;
    // The inbox the in-flight initPromise is initializing, so a concurrent request for a
    // DIFFERENT inbox is not handed the wrong inbox's device.
    this.initPromiseInboxId = null;
    // Shared outbound-placement lock across ALL useCallSession instances / entry points, so a
    // second entry point can't place a second PSTN call during the pre-accept window.
    this.isPlacingOutbound = false;
    // Monotonic teardown counter. A join spans three macrotask awaits (mic preflight, device
    // init, the REST claim) before it can dial, and the End button stays live throughout, so
    // the agent can end the call while the join is still in flight. endClientCall() cannot
    // reach that in-flight leg - device._activeCall is null until device.connect() resolves,
    // and a Pending Call early-returns from Call#_disconnect (SDK 2.12.4 call.js:1242-1249) -
    // so the join has to notice the teardown itself. runJoinFlow snapshots this before its
    // first await and re-checks after every one; a bump means "abandon, do not re-adopt".
    this.teardownGeneration = 0;
    // Call SIDs the agent explicitly DECLINED. A decline must abort a join that is already in
    // flight for THAT call - the agent can hit Accept, park on the microphone prompt, then hit
    // Decline on the same row, and without this the join keeps running on this singleton and
    // puts the agent live on a call they refused. Kept per call SID rather than bumping
    // teardownGeneration, which is global: declining one queued offer must never abort a
    // concurrent join for a different call. Bounded by REJECTED_CALL_SID_LIMIT.
    this.rejectedCallSids = new Set();
    // The listeners WE attached per Call, so a torn-down leg can be fully unwired. The SDK
    // never removes them (Call#_cleanupEventListeners only drops its own), and a Call
    // outlives our reference to it, so a stale handler on a dead leg could still write call
    // state. Weakly keyed so a dropped Call is still collectable.
    this.callListeners = new WeakMap();
    this.store = null;
    this.tabSessionId = null;
  }

  // ------------------- Server APIs -------------------
  // eslint-disable-next-line class-methods-use-this
  initiateCall(contactId, inboxId, options = {}) {
    return ContactsAPI.initiateCall(contactId, inboxId, options).then(
      r => r.data
    );
  }

  leaveConference(inboxId, conversationId) {
    if (!inboxId) throw new Error('Inbox ID is required to leave a conference');
    if (!conversationId)
      throw new Error('Conversation ID is required to leave a conference');
    return axios
      .delete(`${this.baseUrl()}/inboxes/${inboxId}/conference`, {
        params: { conversation_id: conversationId },
      })
      .then(r => r.data);
  }

  conferenceJoinFailed(inboxId, conversationId) {
    if (!inboxId)
      throw new Error('Inbox ID is required to report a failed join');
    if (!conversationId)
      throw new Error('Conversation ID is required to report a failed join');
    return axios
      .post(`${this.baseUrl()}/inboxes/${inboxId}/conference_join_failed`, {
        conversation_id: conversationId,
        // Same per-tab nonce the join claim was made under (matches joinConference's
        // joined_session). The backend releases the claim only when this matches the
        // CURRENT claim, so a stale report from a superseded attempt (a newer tab/user
        // re-claimed) no-ops instead of wiping the fresh claim.
        client_session: this.getTabSessionId(),
      })
      .then(r => r.data);
  }

  // Calls the agent can still act on after a refresh: inbox-wide ringing offers
  // plus the agent's own in-progress calls. Used by the boot hydrate.
  // `this.url` (not baseUrl()) because active_calls is a COLLECTION action on
  // `resource :voice`, so the routed path carries the resource segment:
  // /api/v1/accounts/:id/voice/active_calls. The sibling calls below are inbox
  // members declared on `resources :inboxes`, which genuinely has no voice segment -
  // that is why they use baseUrl() and this one must not.
  fetchActiveCalls() {
    return axios.get(`${this.url}/active_calls`).then(r => r.data);
  }

  joinConference(params) {
    const conversationId = params.conversation_id || params.conversationId;
    const inboxId = params.inbox_id || params.inboxId;
    const callSid = params.call_sid || params.callSid;
    if (!inboxId) throw new Error('Inbox ID is required to join a call');
    if (!conversationId)
      throw new Error('Conversation ID is required to join a call');
    return axios
      .post(`${this.baseUrl()}/inboxes/${inboxId}/conference`, {
        conversation_id: conversationId,
        call_sid: callSid,
        // Same per-tab nonce the Twilio token was issued under: the backend stores it
        // on the join claim and lets only this tab retry the claim.
        client_session: this.getTabSessionId(),
      })
      .then(r => r.data);
  }

  // Consult (warm transfer): dial a third party into the live call, merge them in, or drop them.
  // REST ONLY - the agent's own WebRTC leg is untouched (no Device connect). Merge and Drop cancel
  // any in-flight DTMF first: a handed-over batch keeps playing (~250ms/digit) out of the
  // RTCDTMFSender buffer, and both release the patient while it may still be running, so without
  // this the remaining tones would reach the patient instead of the third party.
  // call_sid is the per-call identity, not decoration: a conversation is reused across calls, so
  // conversation_id alone lets a delayed request act on whatever call is live NOW. The server compares
  // it against the contact leg's sid and refuses a mismatch.
  consultAdd(inboxId, conversationId, number, callSid) {
    if (!inboxId) throw new Error('Inbox ID is required to add a consult');
    if (!conversationId)
      throw new Error('Conversation ID is required to add a consult');
    return axios
      .post(`${this.baseUrl()}/inboxes/${inboxId}/consult`, {
        conversation_id: conversationId,
        call_sid: callSid,
        number,
      })
      .then(r => r.data);
  }

  consultMerge(inboxId, conversationId, callSid) {
    if (!inboxId) throw new Error('Inbox ID is required to merge a consult');
    if (!conversationId)
      throw new Error('Conversation ID is required to merge a consult');
    this.cancelDigits();
    return axios
      .post(`${this.baseUrl()}/inboxes/${inboxId}/consult_merge`, {
        conversation_id: conversationId,
        call_sid: callSid,
      })
      .then(r => r.data);
  }

  consultDrop(inboxId, conversationId, callSid) {
    if (!inboxId) throw new Error('Inbox ID is required to drop a consult');
    if (!conversationId)
      throw new Error('Conversation ID is required to drop a consult');
    this.cancelDigits();
    return axios
      .delete(`${this.baseUrl()}/inboxes/${inboxId}/consult`, {
        params: { conversation_id: conversationId, call_sid: callSid },
      })
      .then(r => r.data);
  }

  // Reject is handled client-side by not joining and clearing state

  getToken(inboxId) {
    if (!inboxId) return Promise.reject(new Error('Inbox ID is required'));
    return axios
      .get(`${this.baseUrl()}/inboxes/${inboxId}/conference_token`, {
        params: { client_session: this.getTabSessionId() },
      })
      .then(r => r.data);
  }

  // Per-tab Twilio identity nonce so a second tab cannot steal the first tab's Device
  // registration. Generated fresh per page load and memoized on the instance only, so
  // initializeDevice and the tokenWillExpire refresh always send the same value.
  // Deliberately NOT persisted: browsers clone sessionStorage into duplicated tabs (and
  // same-origin window.open) per the HTML spec, which would collapse the per-tab identity.
  // A page refresh re-registers under a fresh identity, which is harmless because nothing
  // dials the client identity directly.
  getTabSessionId() {
    if (this.tabSessionId) return this.tabSessionId;

    const raw = (window.crypto?.randomUUID?.() || getUuid()).replace(/-/g, '');
    this.tabSessionId = `v${raw}`.slice(0, VOICE_TAB_ID_MAX_LENGTH);
    return this.tabSessionId;
  }

  // ------------------- Client (Twilio) APIs -------------------
  async initializeDevice(inboxId, { store } = {}) {
    if (!inboxId) throw new Error('Inbox ID is required to initialize');
    if (store) this.store = store;
    // Coalesce concurrent initializations (e.g. a direct outbound placement racing the WS
    // auto-join). Wait out ANY in-flight init in a LOOP, re-checking each pass: a single `await`
    // is not enough because several waiters for a DIFFERENT inbox would all resume together when
    // the in-flight init settles and each start its own registerDevice, clobbering one another.
    // Reuse the in-flight promise for the SAME inbox; otherwise loop until no init is in flight.
    while (this.initPromise) {
      if (this.initPromiseInboxId === inboxId) return this.initPromise;
      // Intentional sequential wait: we must settle the current in-flight init before re-checking
      // whether another waiter has since started one for our inbox (see the coalescing note above).
      // eslint-disable-next-line no-await-in-loop
      await this.initPromise.catch(() => {});
    }
    // Reuse a warm device only for the SAME inbox (it may have just been registered by another
    // waiter whose init we awaited above). Checked AFTER the loop: registerDevice tears the old
    // device down synchronously at its start, so while an init is in flight the device is never a
    // reusable warm one. The token embeds an inbox-specific TwiML app SID + callback endpoints, so
    // dialing inbox B through inbox A's device would hit A's app and fail to resolve B's
    // conversation; a different inbox re-initializes under its own token. The state check is
    // against the REAL SDK enum (see REUSABLE_DEVICE_STATES): a destroyed or unregistered
    // device must be rebuilt, never handed back as warm.
    if (
      this.initialized &&
      this.device &&
      REUSABLE_DEVICE_STATES.includes(this.device.state) &&
      this.deviceInboxId === inboxId
    )
      return this.device;
    this.initPromiseInboxId = inboxId;
    this.initPromise = this.registerDevice(inboxId);
    try {
      return await this.initPromise;
    } finally {
      this.initPromise = null;
      this.initPromiseInboxId = null;
    }
  }

  async registerDevice(inboxId) {
    if (this.device) this.destroyDevice();

    const { Device } = await import('@twilio/voice-sdk');
    const response = await this.getToken(inboxId);
    const { token, voice_enabled, account_id } = response || {};
    if (!voice_enabled) throw new Error('Voice not enabled for this inbox');
    if (!token) throw new Error('Invalid token');

    this.device = new Device(token, {
      allowIncomingWhileBusy: true,
      disableAudioContextSounds: true,
      appParams: { account_id },
    });

    // Basic listeners
    this.device.removeAllListeners();
    this.device.on('registered', () => {});
    this.device.on('unregistered', () => {});
    // Device-level signaling errors (registration, transport, token). Don't swallow
    // them; surface JOIN_FAILED to the agent only while a call is in flight, otherwise
    // just log (registration noise). SDK 2.x emits no Device-level 'disconnect' - Call
    // teardown is wired per-Call in wireCallLifecycle.
    this.device.on('error', error => {
      // eslint-disable-next-line no-console
      console.error('voice device error', error);
      if (this.activeConnection) {
        this.store?.dispatch('calls/setCallError', {
          code: 'JOIN_FAILED',
          message: error?.message,
        });
      }
    });
    this.device.on('tokenWillExpire', async () => {
      const device = this.device;
      try {
        const r = await this.getToken(inboxId);
        // The device may have been torn down / replaced (e.g. an inbox switch) while this token
        // fetch was in flight; applying inbox A's token to a device now registered for inbox B
        // would recreate the wrong-TwiML-app problem this init flow otherwise prevents.
        if (r?.token && this.device === device) device.updateToken(r.token);
      } catch (error) {
        // Token refresh failed
      }
    });

    await this.device.register();
    this.initialized = true;
    this.deviceInboxId = inboxId;
    return this.device;
  }

  // Register the agent's intent to end the call. Called at the top of every teardown path,
  // including BEFORE the awaited server-side leave in useCallSession's endCall, so a join
  // that is still dialing sees the bump on its very next await instead of one REST
  // round-trip later. See the teardownGeneration note in the constructor for why an
  // in-flight leg cannot simply be cancelled.
  beginTeardown() {
    this.teardownGeneration += 1;
    return this.teardownGeneration;
  }

  // Register the agent's decline of ONE specific call, so a join still in flight for it
  // abandons at its next checkpoint instead of going live. A blank sid is ignored on purpose:
  // storing `undefined` would mark every later join whose sid has not resolved yet as
  // declined.
  markCallRejected(callSid) {
    if (!callSid) return;
    this.rejectedCallSids.add(callSid);
    // A Set iterates in insertion order, so its first entry is always the oldest.
    while (this.rejectedCallSids.size > REJECTED_CALL_SID_LIMIT) {
      this.rejectedCallSids.delete(this.rejectedCallSids.values().next().value);
    }
  }

  isCallRejected(callSid) {
    return Boolean(callSid) && this.rejectedCallSids.has(callSid);
  }

  // Drop a stale decline before a FRESH join for the same sid: an explicit Accept supersedes
  // any earlier Decline of that call, so a re-offered (or recycled) sid never inherits a
  // decision the agent has since reversed.
  clearRejectedCall(callSid) {
    if (!callSid) return;
    this.rejectedCallSids.delete(callSid);
  }

  // Attach a listener to a Call and record it so the leg can later be fully unwired.
  // Tolerates the bare Call doubles used in tests (no on / no once).
  addCallListener(call, event, handler, { once = false } = {}) {
    const bind = once ? call?.once : call?.on;
    if (typeof bind !== 'function') return;
    bind.call(call, event, handler);
    const tracked = this.callListeners.get(call) || [];
    tracked.push([event, handler]);
    this.callListeners.set(call, tracked);
  }

  // Remove ONLY the listeners we added to this Call. Deliberately not removeAllListeners():
  // the SDK wires its own handlers on a Call and dropping those would break its lifecycle.
  unwireCallLifecycle(call) {
    const tracked = this.callListeners.get(call);
    if (!tracked) return;
    this.callListeners.delete(call);
    tracked.forEach(([event, handler]) => {
      // Call extends Node's EventEmitter in SDK 2.x, so 'off' exists; removeListener is the
      // fallback for older emitter builds / test doubles.
      if (typeof call.off === 'function') call.off(event, handler);
      else if (typeof call.removeListener === 'function')
        call.removeListener(event, handler);
    });
  }

  // Hard-drop a leg whose join was abandoned mid-dial (the agent ended the call while we were
  // still dialing, so device.connect() resolved into a call that is already over). Unwire
  // FIRST so the leg's dying 'disconnect' - and any late 'accept' - can no longer write call
  // state, then hang it up so no live mic survives the hangup, then release the slots
  // joinClientCall just re-adopted it into.
  abandonCall(call) {
    if (!call) return;
    this.unwireCallLifecycle(call);
    try {
      call.disconnect?.();
    } catch (error) {
      // An already-closed leg has nothing left to hang up.
    }
    if (this.activeConnection === call) {
      this.activeConnection = null;
      this.activeConnectionTo = null;
    }
  }

  endClientCall() {
    this.beginTeardown();
    // Drop any in-flight join so it can never be handed to a later concurrent caller.
    this.pendingConnection = null;
    this.pendingConnectionTo = null;
    this.activeConnectionTo = null;
    // Keep our own reference: the leg's 'disconnect' handler nulls activeConnection, and we
    // still have to unwire the listeners afterwards.
    const leg = this.activeConnection;
    try {
      // Disconnect active connection first
      if (leg) {
        leg.disconnect();
        this.activeConnection = null;
      }

      // Disconnect all connections on the device
      if (this.device) {
        this.device.disconnectAll();
      }
    } catch (error) {
      this.activeConnection = null;
      try {
        if (this.device) {
          this.device.disconnectAll();
        }
      } catch (fallbackError) {
        // ignore
      }
    }
    // The leg is gone and the SDK never removes our listeners, so drop them; otherwise a
    // late 'accept' / 'sample' from the dying leg can still dispatch call state for a call
    // that is over. Done AFTER disconnect() so a synchronous 'disconnect' still gets to
    // clear the store's active call through its own handler.
    this.unwireCallLifecycle(leg);
  }

  destroyDevice() {
    // Same reason as endClientCall: the device (and every Call on it) is going away, so our
    // listeners must go with it.
    this.unwireCallLifecycle(this.activeConnection);
    try {
      if (this.device) {
        this.device.destroy?.();
      }
    } catch (_) {
      // ignore
    } finally {
      this.activeConnection = null;
      this.pendingConnection = null;
      this.activeConnectionTo = null;
      this.pendingConnectionTo = null;
      this.device = null;
      this.initialized = false;
      this.deviceInboxId = null;
    }
  }

  async joinClientCall({ To, conversationId, inboxId, callSid, callMeta }) {
    if (!this.device || !this.initialized) throw new Error('Twilio not ready');
    if (!To) throw new Error('Missing To');

    const normalizedTo = String(To);

    // Return an already-active OR in-flight connection when it dials the SAME conference
    // (idempotency: a direct outbound join racing the WS auto-join, or a double Reconnect;
    // both dial the identical conference friendly name for one call). device.connect() is
    // async, so the pending promise is captured synchronously - without that a concurrent
    // caller would pass the guard before activeConnection is set and open a SECOND leg.
    // A join for a DIFFERENT conference while one is active/pending must NOT reuse the
    // wrong leg (that would phantom-answer the other call): throw so the caller
    // (runJoinFlow) releases its just-made server claim instead.
    if (this.activeConnection) {
      if (this.activeConnectionTo === normalizedTo)
        return this.activeConnection;
      throw new Error('Another call is already active');
    }
    if (this.pendingConnection) {
      if (this.pendingConnectionTo === normalizedTo)
        return this.pendingConnection;
      throw new Error('A different call is already connecting');
    }

    const params = { To: normalizedTo, is_agent: 'true' };
    if (conversationId) params.conversation_id = String(conversationId);

    // device.connect() returns Promise<Call> and resolves BEFORE getUserMedia runs (inside
    // the Call's accept()), so a mic / getUserMedia / pre-answer failure surfaces as a Call
    // 'error' emitted before 'accept', not as a rejection here. Capture the pending promise
    // (+ its To) first so a concurrent same-To caller returns it, then await it to hold the
    // real Call (not the Promise) and to wire the failed-join report.
    this.pendingConnectionTo = normalizedTo;
    this.pendingConnection = this.device.connect({ params });
    let call;
    try {
      call = await this.pendingConnection;
    } finally {
      this.pendingConnection = null;
      this.pendingConnectionTo = null;
    }
    this.activeConnectionTo = normalizedTo;
    this.activeConnection = call;
    this.wireCallLifecycle(call, {
      inboxId,
      conversationId,
      callSid,
      callMeta,
    });

    // A synchronous PeerConnection "SDK is disconnected" error during accept() closes the
    // Call before the once('error') listener above can attach (the SDK documents this race
    // at call.js:1207-1220), so the Call resolves already-closed and emits no 'error'. Run
    // the same failed-join cleanup the error handler does. status() is missing on bare test
    // mocks, so guard the call.
    if (
      typeof call?.status === 'function' &&
      call.status() === 'closed' &&
      inboxId &&
      conversationId
    ) {
      this.handleFailedJoin(call, {
        inboxId,
        conversationId,
        callSid,
        callMeta,
      });
    }
    return call;
  }

  // Wire the resolved Call's lifecycle to store state.
  // - 'disconnect' is the SDK 2.x Call-level teardown (the Device never emits one), so it
  //   clears the active-call state on a normal hang-up. Scoped to THIS call so it never
  //   fights a connection that has since replaced it, nor re-fires after a failed-join
  //   cleanup already nulled activeConnection.
  // - A Call 'error' before 'accept' means the local leg never connected (mic denied 31401,
  //   getUserMedia 31402, or a pre-answer ICE failure): release the claim and surface
  //   JOIN_FAILED. Errors after 'accept' are mid-call and handled by 'disconnect'.
  wireCallLifecycle(call, { inboxId, conversationId, callSid, callMeta = {} }) {
    if (!call) return;

    this.addCallListener(call, 'disconnect', () => {
      if (this.activeConnection !== call) return;
      this.activeConnection = null;
      this.activeConnectionTo = null;
      // Nothing on a closed leg is worth observing, and the SDK leaves our listeners
      // attached, so drop them before handing off to the store.
      this.unwireCallLifecycle(call);
      this.store?.dispatch('calls/clearActiveCall');
    });

    // Audio-health diagnostics (sample levels + quality warnings) on the live Call.
    this.wireCallDiagnostics(call);

    if (!call.once) return;

    let accepted = false;
    this.addCallListener(
      call,
      'accept',
      () => {
        // Record the media connect even when the guard below rejects this leg, so a LATER
        // Call 'error' is still classified as mid-call rather than as a failed join.
        accepted = true;
        // Only the CURRENT connection may paint "in progress". A leg that has since been
        // replaced, or one whose join was abandoned mid-dial (the agent ended the call while
        // we were still dialing), must not write active-call state - mirrors the guards in
        // the 'disconnect' handler and handleFailedJoin.
        if (this.activeConnection !== call) return;
        // The media leg is live now ('accept' fires after getUserMedia + answer), so this
        // is the only safe point to mark the call active: a mic/media failure resolves
        // connect() but never emits 'accept', so it can no longer paint "in progress".
        // setActiveCall also clears any prior callError (success supersedes).
        this.store?.dispatch('calls/setActiveCall', {
          ...callMeta,
          callSid: callSid || callMeta?.callSid,
          conversationId,
          inboxId,
          isJoined: true,
          // A rejoin passes the true call start (call_started_at*1000) so the duration
          // shows real elapsed time; a fresh join passes no startedAt -> stamp now.
          startedAt: callMeta?.startedAt || Date.now(),
        });
        // Clear ONLY the answered ring, keyed by callSid: a blank dispatch would wipe the
        // whole incoming-call queue and drop a co-pending second ring. Guard against a
        // blank/undefined sid so an accept without a resolvable sid never triggers that
        // whole-queue wipe.
        const acceptedSid = callSid || callMeta?.callSid;
        if (acceptedSid) {
          this.store?.dispatch('calls/clearIncomingCall', acceptedSid);
        }

        // No reachable speaker => the agent will hear nothing; surface it right away.
        if (!this.hasAudioOutput()) {
          this.store?.dispatch('calls/setCallError', {
            code: 'NO_AUDIO_OUTPUT',
          });
        }
      },
      { once: true }
    );
    this.addCallListener(
      call,
      'error',
      error => {
        if (accepted) return;
        this.handleFailedJoin(call, {
          inboxId,
          conversationId,
          callSid,
          callMeta,
          error,
        });
      },
      { once: true }
    );
  }

  // Audio diagnostics for a connected Call. Twilio emits ~1 'sample' per second once media
  // flows. Dead audio is detected from the INBOUND RTP counters, NOT from amplitude: an
  // audioInputLevel and audioOutputLevel of zero is also exactly what an ordinary quiet
  // moment looks like (agent muted or simply not talking while the caller is silent), so
  // flagging on amplitude raised a false "no audio" banner during normal pauses. Media that
  // stops ARRIVING is the real starvation signal; the distinct "no output device exists"
  // case stays with hasAudioOutput() on accept.
  // 'warning' events (jitter / rtt / etc.) are logged as breadcrumbs, not surfaced as hard
  // errors - they are usually transient.
  wireCallDiagnostics(call) {
    if (!call?.on) return;

    // Cumulative inbound counter as of the previous sample. Null until the first readable
    // sample establishes a baseline: a single reading cannot show whether media is advancing.
    let lastInboundTotal = null;
    let stalledSamples = 0;
    // Surface NO_AUDIO_OUTPUT ONCE per starvation episode, then re-arm only after media
    // recovers - not every STALLED_MEDIA_SAMPLE_LIMIT samples for the rest of the call (which
    // churned a new error object + reactivity cycle each time).
    let audioErrorFired = false;
    this.addCallListener(call, 'sample', sample => {
      const inbound = readInboundMedia(sample);
      // This sample carries no RTP counters, so it says nothing about media health: neither
      // flag nor retract on it.
      if (!inbound) return;

      let advanced;
      if (inbound.cumulative !== undefined) {
        advanced =
          lastInboundTotal === null || inbound.cumulative > lastInboundTotal;
        lastInboundTotal = inbound.cumulative;
      } else {
        // Already a per-second delta, so anything above zero is media arriving right now.
        advanced = inbound.delta > 0;
      }

      if (!advanced) {
        stalledSamples += 1;
        if (stalledSamples >= STALLED_MEDIA_SAMPLE_LIMIT && !audioErrorFired) {
          audioErrorFired = true;
          this.store?.dispatch('calls/setCallError', {
            code: 'NO_AUDIO_OUTPUT',
          });
        }
        return;
      }

      stalledSamples = 0;
      // Media recovered: retract the NO_AUDIO_OUTPUT banner we raised for this episode - but
      // only when WE raised it (audioErrorFired), so an accept-time "no speaker" warning that
      // will not recover via samples is left intact. clearCallErrorByCode leaves any other
      // error and the retry context untouched.
      if (audioErrorFired) {
        audioErrorFired = false;
        this.store?.dispatch('calls/clearCallErrorByCode', 'NO_AUDIO_OUTPUT');
      }
    });

    // SDK media warnings. Every one is logged, and the four that describe a REAL audio-path
    // failure (see SDK_WARNING_TO_CALL_WARNING) are also surfaced to the agent as a toast and
    // reported for RTC diagnosis. The rest (jitter / rtt / mos / packet loss) stay breadcrumbs:
    // they fire on ordinary network variance, and toasting them would teach agents to ignore
    // the alert entirely.
    //
    // Raised ONCE per episode per warning name: the SDK re-raises while the condition persists,
    // and 'warning-cleared' re-arms it, so a genuine recurrence alerts again but a continuous
    // problem does not spam. Tracked per Call, so a new leg starts clean.
    const activeWarnings = new Set();

    this.addCallListener(call, 'warning', (name, data) => {
      // Console-only on purpose, and NOT an oversight to "fix" by adding Sentry: this
      // deployment observes errors through Google Error Reporting, which ingests SERVER logs
      // (Rails writes to stdout -> Cloud Logging), and the browser has no path into that.
      // Frontend Sentry is gated on SENTRY_FRONTEND_DSN / SENTRY_DSN (entrypoints/dashboard.js)
      // which is unset here, so every Sentry.* call from the browser is a silent no-op.
      // `data` carries the breached threshold and the sample values the SDK judged on, so log
      // it too - it is what an engineer needs when an agent reports a bad call.
      // eslint-disable-next-line no-console
      console.warn('voice call warning', name, {
        callSid: call.parameters?.CallSid || null,
        threshold: data?.threshold || null,
        values: data?.values ?? data?.value ?? null,
      });

      const code = SDK_WARNING_TO_CALL_WARNING[name];
      if (!code || activeWarnings.has(name)) return;
      activeWarnings.add(name);

      // Our own sample-based detector already banners the inbound case, so skip the toast to
      // avoid announcing one problem twice (the banner persists and is the better surface).
      const alreadyBanneredInbound =
        code === CALL_WARNING_CODES.AGENT_CANNOT_HEAR && audioErrorFired;
      if (!alreadyBanneredInbound) {
        this.store?.dispatch('calls/setCallWarning', code);
      }
    });

    // The SDK retracts a warning when the condition recovers. Re-arm so the SAME problem
    // occurring again later is reported again rather than silently swallowed for the rest of
    // the call.
    this.addCallListener(call, 'warning-cleared', name => {
      activeWarnings.delete(name);
    });
  }

  // Send one DTMF tone on the agent's live leg, so an agent can work a phone menu: the
  // language line and physician's offices both answer with an IVR, and without this there is
  // no way to answer their prompts at all.
  //
  // This transmits on the AGENT leg, not the contact's, which is the only leg we hold. That
  // still reaches the far end: Call#sendDigits emits RFC 4733 telephony events through
  // RTCDTMFSender (see the SDK's call.js - the tone you hear locally is a cosmetic
  // "mock DTMF" click and is NOT what travels), and Twilio relays those from one conference
  // participant to the others. This is the same topology and the same SDK call Twilio's own
  // Flex dialpad uses, which it documents as sending tones "to all parties on the call".
  //
  // Consequences of "all parties", both deliberate and worth knowing:
  //   - Everyone in the room hears the tones. There is no Twilio API to aim DTMF at a single
  //     participant (the Participants REST resource has no DTMF parameter at all), so on a
  //     three-way call the contact hears the agent working the interpreter's menu.
  //   - The conference is recorded from the start, so the digits land in the recording as
  //     decodable tones. Anything an agent keys into an IVR is therefore in the recording.
  //
  // Pass MULTI-CHARACTER strings, not one character per press. The SDK forwards the whole
  // string to a single insertDTMF, which the browser queues correctly, whereas one insertDTMF
  // per character REPLACES the previous one's pending buffer and silently drops digits. The
  // caller owns that batching; see the DTMF notes in FloatingCallWidget.
  //
  // The return value means "there was a live leg, the characters were legal, and the SDK
  // accepted them synchronously". It is NOT a delivery receipt: Call#sendDigits returns void
  // and the tones play out asynchronously, so nothing here can confirm the far end heard them.
  // Callers should read it as "handed over", which is enough to keep the UI from claiming more
  // than happened. Never throws: this is wired to a click handler, and a dead leg or a
  // rejected character must not surface as a broken button mid-call.
  sendDigits(digits) {
    const leg = this.activeConnection;
    if (!leg || typeof leg.sendDigits !== 'function') return false;
    // The SDK throws InvalidArgumentError on anything outside [0-9*#w], so validate before
    // handing it over. '+' is the specific trap: it is legitimate when composing a number to
    // dial, so any keypad shared with the dialer will offer it, and it would throw here.
    //
    // NARROWER than the SDK on purpose: 'w' is its half-second pause, which no caller here can
    // produce (the keypad has 12 keys and none of them is 'w') and which nothing needs. Allowing
    // it would only widen what a future caller could smuggle into a tone sequence.
    if (!/^[0-9*#]+$/.test(String(digits ?? ''))) return false;

    // Watch for a SYNCHRONOUS error emit while the digits are handed over. When the SDK has no
    // RTCDTMFSender it falls back to the signaling channel, and if that is disconnected it does
    // NOT throw - it emits 'error' (ConnectionError 31005) and returns normally. The lifecycle
    // error listener in wireCallLifecycle ignores everything after accept, so without this the
    // batch looked accepted, the readout showed it, and the far end received none of it.
    let emitted = null;
    const observeError = error => {
      emitted = error;
    };
    leg.on?.('error', observeError);

    try {
      leg.sendDigits(String(digits));
      if (emitted) {
        // eslint-disable-next-line no-console
        console.error(
          'voice sendDigits rejected by the SDK',
          {
            length: String(digits ?? '').length,
            callSid: leg.parameters?.CallSid ?? null,
          },
          emitted
        );
        return false;
      }
      return true;
    } catch (error) {
      // Console-only, consistent with the rest of this file: browser errors have no path into
      // Google Error Reporting, which ingests server logs. Reaching here means our validation
      // and the SDK's disagree, which is a bug in the regex above rather than agent error.
      //
      // The digits themselves are deliberately NOT logged. What an agent keys into a provider's
      // menu is routinely a member ID, a date of birth or a PIN, and console history outlives
      // the call: it ends up in screenshots, support bundles and session-capture tooling. The
      // length plus the call is enough to debug a validation mismatch.
      // eslint-disable-next-line no-console
      console.error(
        'voice sendDigits failed',
        {
          length: String(digits ?? '').length,
          callSid: leg.parameters?.CallSid ?? null,
        },
        error
      );
      return false;
    } finally {
      leg.off?.('error', observeError);
    }
  }

  // Cancel tones the browser has already accepted but not yet played, so ending a call also
  // stops the digits it was mid-way through sending.
  //
  // Clearing our own pending string is not enough. Once a batch is handed over it lives in the
  // RTCDTMFSender's tone buffer, and the leg stays up for the whole leaveConference round trip -
  // so the tail of "23" could still reach a menu after the agent hung up.
  //
  // The SDK offers no cancel: Call#sendDigits('') builds an empty sequence and its
  // `if (dtmf && dtmf.length)` guard skips insertDTMF entirely, so it is a no-op rather than a
  // clear. Per the WebRTC spec, insertDTMF('') is the documented way to empty the buffer (the
  // tone already playing finishes; nothing after it does), and the only handle on that sender is
  // the SDK's private _mediaHandler. That coupling is deliberate and contained here: every step
  // is optional-chained and the whole thing is wrapped, so an SDK that moves or renames it
  // degrades to "could not cancel" rather than throwing inside a teardown path.
  cancelDigits() {
    const leg = this.activeConnection;
    if (!leg) return false;
    try {
      // _mediaHandler is the only handle on the RTCDTMFSender and the SDK exposes no cancel.
      // Guarded on every step, see the note on this method.
      // eslint-disable-next-line no-underscore-dangle
      const sender = leg._mediaHandler?.getOrCreateDTMFSender?.();
      if (typeof sender?.insertDTMF !== 'function') return false;
      sender.insertDTMF('');
      return true;
    } catch (error) {
      // Never rethrow: this runs while a call is being torn down.
      // eslint-disable-next-line no-console
      console.warn('voice cancelDigits failed', error);
      return false;
    }
  }

  // Whether the Device has at least one reachable audio output. Unknown (no audio API in
  // this SDK build / a bare test mock) is treated as OK so we never raise a false alarm.
  hasAudioOutput() {
    const available = this.device?.audio?.availableOutputDevices;
    if (available && typeof available.size === 'number') {
      return available.size > 0;
    }
    return true;
  }

  // Release the backend claim, then tear down the dead local leg. SDK 2.x emits no
  // Device-level 'disconnect' for a failed join, so nothing else clears activeConnection
  // - a retry would reuse this dead Call via the joinClientCall early-return and the UI
  // would stay stuck "joined". The === call guard scopes EVERY side effect to THIS call:
  // a leg that has since been replaced, or one the agent already ended (teardown nulls
  // activeConnection), must not release the CURRENT claim, resurrect a Retry offer, or
  // surface JOIN_FAILED for a call that is over. It also makes the function idempotent, so
  // the synchronous status-closed path in joinClientCall and a later Call 'error' cannot
  // double-report the same dead leg.
  handleFailedJoin(
    call,
    { inboxId, conversationId, callSid, callMeta = {}, error } = {}
  ) {
    if (this.activeConnection !== call) return;
    this.activeConnection = null;
    this.activeConnectionTo = null;
    // The leg is dead; drop our listeners so its late 'disconnect' / 'sample' events cannot
    // keep writing call state (the SDK does not remove them for us).
    this.unwireCallLifecycle(call);
    if (inboxId && conversationId) {
      this.conferenceJoinFailed(inboxId, conversationId).catch(() => {});
    }
    this.store?.dispatch('calls/clearActiveCall');
    // An OUTBOUND leg that fails pre-accept (mic / getUserMedia / pre-answer ICE) fires the Call
    // 'error' asynchronously AFTER device.connect() already resolved, so joinOutboundConference
    // has returned and its catch never set the retry context - the customer is on hold server-side
    // with only Dismiss showing. Restore the Retry context here (callMeta carries the conference
    // we dialed for outbound) so the widget offers Retry, not just Dismiss.
    if (callMeta?.isOutbound && callMeta?.conference_sid) {
      this.store?.dispatch('calls/setRetryableOutbound', {
        conferenceSid: callMeta.conference_sid,
        callSid: callSid || callMeta.callSid,
        conversationId,
        inboxId,
        callMeta,
      });
    }
    this.store?.dispatch('calls/setCallError', {
      code: 'JOIN_FAILED',
      message: error?.message,
    });
  }

  // Maps the SDK 2.x Device.State enum ({ destroyed, unregistered, registering, registered })
  // onto our own vocabulary. There is no 'error' state to map - a Device reports failures
  // through its 'error' event, not through state.
  getDeviceStatus() {
    if (!this.device) return 'not_initialized';
    const s = this.device.state;
    switch (s) {
      case 'registered':
        return 'ready';
      case 'registering':
        return 'connecting';
      case 'unregistered':
        return 'disconnected';
      case 'destroyed':
        return 'terminated';
      default:
        return s;
    }
  }
}

export default new VoiceAPI();
